export interface HealthResponse {
  status: 'ok' | 'error';
  time: string;
}

export interface ApiError {
  error: string;
  details?: string;
}

// Naxxramas encounter IDs. Vanilla (Era) WCL uses 51xxx; classic (WotLK) uses
// 1xxx. We accept either since the zone is the same boss roster.
export const NAXX_ENCOUNTER_IDS: ReadonlySet<number> = new Set([
  // Vanilla / Era (vanilla.warcraftlogs.com) — primary target of this tool.
  51107, // Anub'Rekhan
  51110, // Faerlina
  51116, // Maexxna
  51117, // Noth the Plaguebringer
  51112, // Heigan the Unclean
  51115, // Loatheb
  51113, // Instructor Razuvious
  51109, // Gothik the Harvester
  51121, // The Four Horsemen
  51118, // Patchwerk
  51111, // Grobbulus
  51108, // Gluth
  51120, // Thaddius
  51119, // Sapphiron
  51114, // Kel'Thuzad
  // Classic / WotLK (classic.warcraftlogs.com) — kept for completeness.
  1107, 1110, 1116, 1117, 1112, 1115, 1113, 1109, 1121, 1118, 1111, 1108, 1120, 1119, 1114,
]);

// Naxxramas zone ID is identical (2006) on both vanilla and classic WCL.
export const NAXX_ZONE_ID = 2006;

export interface WclFight {
  id: number;            // fight id within the report
  encounterID: number;   // 0 if trash; matches NAXX_ENCOUNTER_IDS for boss fights
  name: string;          // boss name
  startTime: number;     // ms from report start
  endTime: number;       // ms from report start
  durationSec: number;
  kill: boolean;
  difficulty: number;    // 0=normal in classic
  friendlyPlayerIds: number[]; // actor ids participating in the fight
}

export interface WclPlayer {
  id: number;
  name: string;
  className: string;     // "Warrior", "Mage", ...
  server: string;
}

export interface ReportResponse {
  code: string;
  title: string;
  startTime: number;
  endTime: number;
  zone: { id: number; name: string } | null;
  fights: WclFight[];
  players: WclPlayer[];
}

export interface PlayerInfoResponse {
  code: string;
  fightID: number;
  playerID: number;
  fightName: string;
  fightStart: number;     // ms from report start
  fightEnd: number;       // ms from report start
  fightDurationSec: number;
  fightKill: boolean;
  // Raw CombatantInfo events from WCL. Shape varies per game version, so phase
  // 1.4 returns them as-is; the frontend parses them via shared/parsing.ts.
  combatantInfoEvents: unknown[];
  // WCL table(dataType: Buffs) result — covers raid buffs (Kings/Salv/MotW/...)
  // that COMBATANT_INFO doesn't include in vanilla. Shape is loose JSON.
  buffsTable: unknown;
}

// ---- Parsed CombatantInfo (frontend uses these) ----

// Order matches the WCL gear array (vanilla / classic schema).
export const EQUIPMENT_SLOT_NAMES = [
  'Head', 'Neck', 'Shoulder', 'Shirt', 'Chest', 'Waist', 'Legs', 'Feet',
  'Wrist', 'Hands', 'Finger 1', 'Finger 2', 'Trinket 1', 'Trinket 2',
  'Back', 'Main Hand', 'Off Hand', 'Ranged', 'Tabard',
] as const;

export interface EquippedItem {
  slot: string;          // human-readable slot name
  slotIndex: number;     // index in WCL gear array
  itemId: number;        // 0 = empty
  quality: number;       // 1 (common) ~ 5 (legendary)
  itemLevel: number;
  icon: string;          // wowhead icon filename
  permanentEnchant?: number;   // enchant id (not spell id)
  temporaryEnchant?: number;   // weapon oils etc.
  setID?: number;
}

export interface Aura {
  abilityId: number;
  name: string;
  icon: string;
  source: number;        // WCL actor id of the aura caster
  stacks: number;
  // Provenance and uptime info (added in phase 1.5.6).
  // 'starting' = present in the COMBATANT_INFO event at fight start.
  // 'persistent' = uptime ≥ 70% of fight from the buff table (likely raid buff).
  // 'transient' = lower uptime (procs, cooldowns, on-use trinkets).
  // 'assumed' = manually toggled by the user as a pre-pull raid buff that the
  //   vanilla combat log didn't capture.
  origin?: 'starting' | 'persistent' | 'transient' | 'assumed';
  uptimeMs?: number;
  uses?: number;
}

export interface PlayerStats {
  strength: number;
  agility: number;
  stamina: number;
  intellect: number;
  spirit: number;
  armor: number;
}

export interface TalentTreePoints {
  arms: number;
  fury: number;
  protection: number;
}

export type Spec = 'fury' | 'arms' | 'protection';

// fieldName from warrior_talents.json -> points spent on that talent.
export type TalentBuild = Record<string, number>;

export interface ParsedPlayerInfo {
  gear: EquippedItem[];
  auras: Aura[];
  stats: PlayerStats;
  treePoints: TalentTreePoints;
  spec: Spec;
  inferredBuild: TalentBuild;   // suggested 51-point allocation
}
